import {WarningAmberRounded} from '@mui/icons-material';
import {Card, Typography} from '@mui/material';
import {Box, Container} from '@mui/system';
import {createFileRoute, useNavigate, useSearch} from '@tanstack/react-router';
import {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'tolgee';

import {theme} from '~constants/theme';
import {abortAllRequests} from '~api/api';
import {orgIdQueryParam, projectIdQueryParam, Route as RouteEnum} from '~utils/routeUtil';
import {useTrackEvent} from '~hooks/analytics.hooks';
import {Project} from '~redux/types/user';
import {annotatorActions} from '~redux/reducers/annotatorReducer';
import {imageDataActions} from '~redux/reducers/imageReducer';
import {
  isProjectInUserData,
  selectDefaultProjectId,
  selectLandingPage,
  selectProjects,
} from '~redux/reducers/userReducer';
import {setCurrentProjectId} from '~redux/actions/userActions';
import {useAppDispatch, useAppSelector} from '~redux/index';

import {LayoutContent, LayoutHeader} from '~components/_layout/layoutComponents';
import {MainTitleBar} from '~components/header/MainTitleBar';
import {ProjectDetails} from '~components/user/ProjectDetails';

export const Route = createFileRoute('/invalid-project')({
  component: InvalidProjectPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      [projectIdQueryParam]: (search[projectIdQueryParam] as string) || undefined,
      [orgIdQueryParam]: (search[orgIdQueryParam] as string) || undefined,
    };
  },
});

function InvalidProjectPage(): JSX.Element {
  const {t} = useTranslation('user');
  const navigate = useNavigate();
  const search = useSearch({from: '/invalid-project'});
  const projectId = search[projectIdQueryParam];
  const projects: Project[] = useAppSelector(selectProjects);
  const defaultProjectId = useAppSelector(selectDefaultProjectId);
  const dispatch = useAppDispatch();
  const userLandingPage = useAppSelector(selectLandingPage);
  const isValidProject = useMemo(() => isProjectInUserData(projects, projectId as string), [projects, projectId]);
  const trackEvent = useTrackEvent();
  const byDefaultProjectAndAlphabetically = useCallback(
    (a: Project, b: Project) => {
      if (a.id === defaultProjectId) return -1;
      if (b.id === defaultProjectId) return 1;
      if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
      if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
      return 0;
    },
    [defaultProjectId],
  );

  const sortedProjects = [...projects].sort(byDefaultProjectAndAlphabetically);

  const handleProjectSelect = useCallback(
    async (projectId: string) => {
      trackEvent('invalid_project_page_project_selected', {projectId});
      dispatch(setCurrentProjectId(projectId));
      dispatch(annotatorActions.setCurrentModel(undefined));
      await abortAllRequests();
      dispatch(imageDataActions.resetImages());
      const route = userLandingPage ? `/${userLandingPage.toLowerCase()}` : RouteEnum.archive;
      await navigate({
        to: route as any,
        search: {
          [projectIdQueryParam]: projectId,
          [orgIdQueryParam]: search[orgIdQueryParam],
        } as any,
      });
    },
    [dispatch, trackEvent, userLandingPage, navigate, search],
  );

  useEffect(() => {
    if (projects.length === 0 || !isValidProject || !projectId) {
      return;
    }

    handleProjectSelect(projectId as string);
  }, [handleProjectSelect, isValidProject, projectId, projects.length]);

  if (isValidProject) {
    return <></>;
  }

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: 0,
        maxWidth: 'none !important',
      }}
    >
      <LayoutHeader
        sx={{
          padding: 0,
          marginLeft: 'auto',
        }}
      >
        <MainTitleBar showOnboardingGuide={false} showBackButton={false} showUserMenu={true} />
      </LayoutHeader>
      <LayoutContent
        sx={{
          padding: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            paddingTop: theme.spacing(10),
            margin: '0 auto',
            gap: theme.spacing(10),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <WarningAmberRounded
              sx={{
                color: theme.palette.warning.light,
              }}
            />
            <Typography variant='h1'>{t('invalidProject.description', {projectId: projectId as string})}</Typography>
          </Box>
          <Card
            sx={{
              display: 'flex',
              gap: 3,
              flexDirection: 'column',
              alignItems: 'center',
              padding: 3,
              borderRadius: theme.spacing(0.5),
            }}
          >
            <Typography variant='h2'>{t('invalidProject.selectProject')}</Typography>
            <Box display='flex' gap={theme.spacing(3)} justifyContent='center' flexWrap='wrap'>
              {sortedProjects.map((project: Project) => (
                <ProjectDetails
                  key={project.id}
                  project={project}
                  handleProjectSelect={handleProjectSelect}
                  simpleView={true}
                />
              ))}
            </Box>
          </Card>
        </Box>
      </LayoutContent>
    </Container>
  );
}
